import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Gavel, FileClock, FileText, UserPlus, CheckCircle2 } from "lucide-react";

interface Props {
  userId: string;
}

interface ActionRow {
  key: string;
  icon: React.ComponentType<{ className?: string }>;
  count: number;
  label: string;
  cta: string;
  urgency: "urgent" | "attention" | "normal";
  onClick: () => void;
}

/**
 * Section 2 — "Needs Your Attention" action items.
 * One aggregate query, surfaces zero-counts as a friendly empty state.
 */
export function ActionItemsPanel({ userId }: Props) {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["shipper-action-items", userId],
    enabled: !!userId,
    staleTime: 30_000,
    queryFn: async () => {
      const { data: loads } = await supabase
        .from("direct_loads")
        .select("id, status")
        .eq("created_by", userId);
      const loadIds = (loads || []).map((l: any) => l.id);
      const postedIds = (loads || []).filter((l: any) => l.status === "posted").map((l: any) => l.id);

      // Bids ready to award (pending bids on posted loads), grouped by load.
      const { data: pendingBids } = postedIds.length
        ? await supabase
            .from("direct_bids")
            .select("direct_load_id")
            .in("direct_load_id", postedIds)
            .eq("status", "pending")
        : { data: [] as any[] };
      const loadsReady = new Set((pendingBids || []).map((b: any) => b.direct_load_id)).size;

      // Quote requests expiring within 24h.
      const now = new Date();
      const in24h = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString();
      const { count: expiringQuotes } = await supabase
        .from("direct_quote_requests")
        .select("id", { count: "exact", head: true })
        .eq("created_by", userId)
        .eq("status", "open")
        .lte("deadline", in24h)
        .gte("deadline", now.toISOString());

      // Dispatches without POD docs (delivered or in-transit but no docs uploaded).
      // Best-effort: count dispatches owned by shipper with status assigned/ready (in flight) — proxy for "needs docs review".
      const { data: dispatches } = await supabase
        .from("direct_dispatches")
        .select("id, status")
        .eq("shipper_id", userId);
      const inFlight = (dispatches || []).filter((d: any) =>
        ["assigned", "ready", "in_transit", "picked_up", "delivered"].includes(d.status),
      );
      let docsPending = 0;
      if (inFlight.length) {
        const dIds = inFlight.map((d: any) => d.id);
        const { data: docs } = await supabase
          .from("direct_dispatch_documents")
          .select("dispatch_id")
          .in("dispatch_id", dIds);
        const withDocs = new Set((docs || []).map((d: any) => d.dispatch_id));
        docsPending = inFlight.filter((d: any) => !withDocs.has(d.id)).length;
      }

      // Pending broker join requests — RLS scopes to this shipper's company already.
      const joinRes: any = await supabase
        .from("shipper_network_join_requests")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending");
      const joinPending = joinRes.count || 0;

      return {
        loadsReady,
        expiringQuotes: expiringQuotes || 0,
        docsPending,
        joinPending: joinPending || 0,
      };
    },
  });

  const rows: ActionRow[] = [
    {
      key: "bids",
      icon: Gavel,
      count: data?.loadsReady ?? 0,
      label: "load(s) with bids ready to award",
      cta: "Review",
      urgency: (data?.loadsReady ?? 0) > 0 ? "urgent" : "normal",
      onClick: () => navigate("/lr-direct/my-opportunities"),
    },
    {
      key: "quotes",
      icon: FileClock,
      count: data?.expiringQuotes ?? 0,
      label: "quote request(s) expiring within 24h",
      cta: "Respond",
      urgency: (data?.expiringQuotes ?? 0) > 0 ? "urgent" : "normal",
      onClick: () => navigate("/lr-direct/my-quotes"),
    },
    {
      key: "docs",
      icon: FileText,
      count: data?.docsPending ?? 0,
      label: "dispatch(es) awaiting documents",
      cta: "View",
      urgency: (data?.docsPending ?? 0) > 0 ? "attention" : "normal",
      onClick: () => navigate("/lr-direct/dispatches"),
    },
    {
      key: "joins",
      icon: UserPlus,
      count: data?.joinPending ?? 0,
      label: "broker join request(s) pending",
      cta: "Decide",
      urgency: (data?.joinPending ?? 0) > 0 ? "attention" : "normal",
      onClick: () => navigate("/lr-direct/network-settings"),
    },
  ];

  const active = rows.filter((r) => r.count > 0);
  const showEmpty = !isLoading && active.length === 0;

  return (
    <section className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-bold text-foreground">Needs Your Attention</h3>
        {!isLoading && (
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {active.length} item{active.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 rounded-md bg-muted/40 animate-pulse" />
          ))}
        </div>
      ) : showEmpty ? (
        <div className="flex items-center gap-3 rounded-md border border-dashed p-4 text-sm text-muted-foreground">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          All caught up — nothing urgent right now.
        </div>
      ) : (
        <ul className="space-y-2">
          {active.map((r) => {
            const Icon = r.icon;
            const dot =
              r.urgency === "urgent"
                ? "bg-red-500"
                : r.urgency === "attention"
                ? "bg-amber-500"
                : "bg-emerald-500";
            return (
              <li
                key={r.key}
                className="flex items-center gap-3 rounded-md border bg-background p-3"
              >
                <span className={`h-2 w-2 flex-shrink-0 rounded-full ${dot}`} aria-hidden />
                <Icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1 text-sm">
                  <span className="font-semibold text-foreground">{r.count}</span>{" "}
                  <span className="text-muted-foreground">{r.label}</span>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={r.onClick}>
                  {r.cta}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
